// Webstaurant spend tracking page
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Card,
  Typography,
  Select,
  DatePicker,
  Table,
  Tag,
  Alert,
  Spin,
  Empty,
  Statistic,
  Row,
  Col,
  Space,
  Tooltip,
  Button,
} from 'antd';
import { ShopOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { Dayjs } from 'dayjs';
import { useMobile } from '../../hooks/useMobile';
import { formatCurrency } from '../../utils/formatters';
import WebstaurantService, {
  WebstaurantData,
  WebstaurantLocation,
  WebstaurantItemSpend,
  WebstaurantStoreDetail,
  WebstaurantWeekDetail,
} from '../../services/WebstaurantService';
import { DateRange } from '../../services/DashboardService';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// 'week' = one week (bucket 0 of the 8-week window ending that week);
// '8week' = the 8-week window averages; 'lastMonth' = last 4 weeks; 'custom' = arbitrary range.
type Mode = 'week' | '8week' | 'lastMonth' | 'ytd' | 'custom';

const TIER_CONFIG: { key: string; name: string; range: string; color: string }[] = [
  { key: 'HIGH_VOLUME', name: 'High Volume', range: '> $35k / wk', color: '#7a1f2a' },
  { key: 'UPPER_MID', name: 'Upper Mid', range: '$25k – $35k / wk', color: '#b8743f' },
  { key: 'LOWER_MID', name: 'Lower Mid', range: '$20k – $25k / wk', color: '#3f7cb8' },
  { key: 'BASE', name: 'Base', range: '< $20k / wk', color: '#6b7280' },
];

const rangeKey = (w: DateRange) => `${w.startDate}_${w.endDate}`;
const weekStartOf = (endISO: string) => dayjs(endISO).subtract(6, 'day').format('YYYY-MM-DD');
const cur = (v: number | null | undefined) => (v != null ? formatCurrency(v) : '—');

// "Jun 15 – 21", cross-month "May 26 – Jun 1", and the year only when it isn't the current year.
const periodLabel = (startISO: string, endISO: string) => {
  const s = dayjs(startISO);
  const e = dayjs(endISO);
  const left = s.format('MMM D');
  const right = s.month() === e.month() ? e.format('D') : e.format('MMM D');
  const year = e.year() !== dayjs().year() ? `, ${e.format('YYYY')}` : '';
  return `${left} – ${right}${year}`;
};

const lastMonthSpend = (l: WebstaurantLocation) =>
  (l.weeklyBreakdown ?? []).slice(0, 4).reduce((s, w) => s + w.spend, 0);

const Webstaurant: React.FC = () => {
  const isMobile = useMobile();

  const [weeks, setWeeks] = useState<DateRange[]>([]);
  // period: 'lastMonth' | '8week' | 'custom' | 'week:<start>_<end>'
  const [period, setPeriod] = useState<string>('');
  const [customRange, setCustomRange] = useState<[Dayjs, Dayjs] | null>(null);
  const [allCategories, setAllCategories] = useState<string[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [data, setData] = useState<WebstaurantData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Per-store full weekly history, loaded on expand. Keyed by location.
  const [detailByLoc, setDetailByLoc] = useState<Record<string, WebstaurantStoreDetail>>({});
  const [detailLoading, setDetailLoading] = useState<Record<string, boolean>>({});
  const [selectedWeekByLoc, setSelectedWeekByLoc] = useState<Record<string, string>>({});

  useEffect(() => {
    (async () => {
      try {
        const [ws, cats] = await Promise.all([
          WebstaurantService.getWeeksWithData(),
          WebstaurantService.getAvailableCategories(),
        ]);
        setWeeks(ws);
        setAllCategories(cats);
        setSelectedCategories(cats);
        if (ws.length > 0) {
          setPeriod(`week:${rangeKey(ws[0])}`); // default: the most recent week
          setCustomRange([dayjs(ws[0].startDate), dayjs(ws[0].endDate)]);
        }
      } catch {
        setError('Failed to load Webstaurant filters. Is data uploaded?');
      }
    })();
  }, []);

  const mode: Mode = period === 'lastMonth' ? 'lastMonth'
    : period === '8week' ? '8week'
    : period === 'ytd' ? 'ytd'
    : period === 'custom' ? 'custom' : 'week';

  // The 8-week window backing the current selection (latest for 8week/lastMonth).
  const selectedWindow = useMemo<DateRange | null>(() => {
    if (period.startsWith('week:')) {
      const k = period.slice(5);
      return weeks.find((w) => rangeKey(w) === k) ?? null;
    }
    return weeks[0] ?? null;
  }, [period, weeks]);

  // The actual date range fetched from the API for this mode.
  const activeRange = useMemo<{ startDate: string; endDate: string } | null>(() => {
    if (mode === 'custom') {
      return customRange
        ? { startDate: customRange[0].format('YYYY-MM-DD'), endDate: customRange[1].format('YYYY-MM-DD') }
        : null;
    }
    if (mode === 'lastMonth') {
      return selectedWindow
        ? { startDate: dayjs(selectedWindow.endDate).subtract(27, 'day').format('YYYY-MM-DD'), endDate: selectedWindow.endDate }
        : null;
    }
    if (mode === 'ytd') {
      return selectedWindow
        ? { startDate: `${dayjs(selectedWindow.endDate).year()}-01-01`, endDate: selectedWindow.endDate }
        : null;
    }
    return selectedWindow ? { startDate: selectedWindow.startDate, endDate: selectedWindow.endDate } : null;
  }, [mode, customRange, selectedWindow]);

  const loadData = useCallback(
    async (startDate: string, endDate: string, categories: string[]) => {
      setLoading(true);
      setError(null);
      try {
        const catParam =
          categories.length === 0 || categories.length === allCategories.length ? undefined : categories;
        setData(await WebstaurantService.getWebstaurantData(startDate, endDate, catParam));
      } catch {
        setError('Failed to load Webstaurant data.');
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [allCategories.length]
  );

  useEffect(() => {
    if (activeRange) loadData(activeRange.startDate, activeRange.endDate, selectedCategories);
  }, [activeRange, selectedCategories, loadData]);

  // Store detail depends on the category filter — drop the cache when it changes.
  useEffect(() => {
    setDetailByLoc({});
    setSelectedWeekByLoc({});
  }, [selectedCategories]);

  const fetchDetail = useCallback(
    async (location: string) => {
      setDetailLoading((m) => ({ ...m, [location]: true }));
      try {
        const catParam =
          selectedCategories.length === 0 || selectedCategories.length === allCategories.length
            ? undefined
            : selectedCategories;
        const d = await WebstaurantService.getStoreDetail(location, catParam);
        setDetailByLoc((m) => ({ ...m, [location]: d }));
        setSelectedWeekByLoc((m) => (m[location] ? m : { ...m, [location]: d.weeks[0]?.weekEnding ?? '' }));
      } catch {
        /* leave unfetched; expand shows empty */
      } finally {
        setDetailLoading((m) => ({ ...m, [location]: false }));
      }
    },
    [selectedCategories, allCategories.length]
  );

  const isWeek = mode === 'week';
  const is8Week = mode === '8week';
  const isRange = mode === 'lastMonth' || mode === 'ytd' || mode === 'custom'; // totals over a span, flat table

  const headerPeriod = useMemo(() => {
    if (!activeRange) return '';
    return isWeek
      ? periodLabel(weekStartOf(activeRange.endDate), activeRange.endDate)
      : periodLabel(activeRange.startDate, activeRange.endDate);
  }, [activeRange, isWeek]);

  const primarySpend = useCallback(
    (l: WebstaurantLocation) =>
      isWeek ? l.thisWeekSpend : is8Week ? l.spendWeeklyAvg : mode === 'lastMonth' ? lastMonthSpend(l) : l.spendTotal,
    [isWeek, is8Week, mode]
  );

  const storesByTier = useMemo(() => {
    const map: Record<string, WebstaurantLocation[]> = {};
    (data?.stores ?? []).forEach((s) => {
      (map[s.tier ?? 'BASE'] = map[s.tier ?? 'BASE'] || []).push(s);
    });
    return map;
  }, [data]);

  // ---- Column helpers ------------------------------------------------------
  const colHead = (main: string, opts?: { sub?: string; tip?: string }) => (
    <div style={{ lineHeight: 1.2 }}>
      {opts?.tip ? (
        <Tooltip title={opts.tip}>
          <span style={{ borderBottom: '1px dotted #bfbfbf', cursor: 'help' }}>{main}</span>
        </Tooltip>
      ) : (
        main
      )}
      {opts?.sub && <div style={{ fontSize: 11, fontWeight: 400, color: '#8c8c8c' }}>{opts.sub}</div>}
    </div>
  );

  const renderTrend = (pct: number | null) => {
    if (pct == null) return <Text type="secondary">—</Text>;
    if (Math.abs(pct) < 0.5) return <Text type="secondary">≈</Text>;
    return (
      <Text type="secondary">
        {pct > 0 ? '▲' : '▼'} {Math.abs(pct).toFixed(0)}%
      </Text>
    );
  };

  const buildColumns = (showSales: boolean): ColumnsType<WebstaurantLocation> => {
    const location: ColumnsType<WebstaurantLocation>[number] = {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
      fixed: isMobile ? undefined : 'left',
      render: (v: string) => <Text strong>{v}</Text>,
    };
    const itemsCol = (dataIndex: keyof WebstaurantLocation): ColumnsType<WebstaurantLocation>[number] => ({
      title: colHead('Items', { tip: 'Number of order line items' }),
      dataIndex,
      key: 'items',
      align: 'right',
      sorter: (a, b) => ((a[dataIndex] as number) ?? 0) - ((b[dataIndex] as number) ?? 0),
    });

    // ---- Single week ----
    if (isWeek) {
      const cols: ColumnsType<WebstaurantLocation> = [location];
      if (showSales)
        cols.push({
          title: colHead('Store Sales', { sub: headerPeriod, tip: 'Store POS sales (Toast + EzCater) for this week' }),
          dataIndex: 'thisWeekSales',
          key: 'thisWeekSales',
          align: 'right',
          render: (v: number | null) => cur(v),
          sorter: (a, b) => (a.thisWeekSales ?? -1) - (b.thisWeekSales ?? -1),
        });
      cols.push({
        title: colHead('Webstaurant Spend', { sub: headerPeriod, tip: 'Webstaurant purchases for this week' }),
        dataIndex: 'thisWeekSpend',
        key: 'thisWeekSpend',
        align: 'right',
        defaultSortOrder: 'descend',
        render: (v: number) => <Text strong>{formatCurrency(v)}</Text>,
        sorter: (a, b) => a.thisWeekSpend - b.thisWeekSpend,
      });
      cols.push(
        {
          title: colHead('vs 8-wk Avg', { tip: "This week's spend vs the average week over the last 8 weeks" }),
          dataIndex: 'trendPct',
          key: 'trendPct',
          align: 'right',
          render: (v: number | null) => renderTrend(v),
          sorter: (a, b) => (a.trendPct ?? 0) - (b.trendPct ?? 0),
        },
        itemsCol('thisWeekItems')
      );
      return cols;
    }

    // ---- 8-week window averages ----
    if (is8Week) {
      const cols: ColumnsType<WebstaurantLocation> = [location];
      if (showSales)
        cols.push({
          title: colHead('Sales / wk', { tip: 'Store POS sales (Toast + EzCater), averaged per week over the 8-week window' }),
          dataIndex: 'weeklySales',
          key: 'weeklySales',
          align: 'right',
          render: (v: number | null) => cur(v),
          sorter: (a, b) => (a.weeklySales ?? -1) - (b.weeklySales ?? -1),
        });
      cols.push(
        {
          title: colHead('Spend / wk', { tip: 'Webstaurant spend, averaged per week over the 8-week window' }),
          dataIndex: 'spendWeeklyAvg',
          key: 'spendWeeklyAvg',
          align: 'right',
          defaultSortOrder: 'descend',
          render: (v: number) => <Text strong>{formatCurrency(v)}</Text>,
          sorter: (a, b) => a.spendWeeklyAvg - b.spendWeeklyAvg,
        },
        {
          title: colHead('Latest Week', { sub: headerPeriodShort(), tip: 'Webstaurant spend in the most recent week' }),
          dataIndex: 'thisWeekSpend',
          key: 'thisWeekSpend',
          align: 'right',
          render: (v: number) => formatCurrency(v),
          sorter: (a, b) => a.thisWeekSpend - b.thisWeekSpend,
        },
        {
          title: colHead('Trend', { tip: 'Most recent week vs the weekly average' }),
          dataIndex: 'trendPct',
          key: 'trendPct',
          align: 'right',
          render: (v: number | null) => renderTrend(v),
          sorter: (a, b) => (a.trendPct ?? 0) - (b.trendPct ?? 0),
        },
        itemsCol('itemCount')
      );
      return cols;
    }

    // ---- Range totals (Last Month / Custom) ----
    const cols: ColumnsType<WebstaurantLocation> = [
      location,
      {
        title: colHead('Total Spend', { sub: headerPeriod, tip: 'Total Webstaurant spend across the selected span' }),
        dataIndex: 'spendTotal',
        key: 'spendTotal',
        align: 'right',
        defaultSortOrder: 'descend',
        render: (v: number) => <Text strong>{formatCurrency(v)}</Text>,
        sorter: (a, b) => a.spendTotal - b.spendTotal,
      },
      {
        title: colHead('Spend / wk', { tip: 'Webstaurant spend averaged per week across the span' }),
        dataIndex: 'spendWeeklyAvg',
        key: 'spendWeeklyAvg',
        align: 'right',
        render: (v: number) => formatCurrency(v),
        sorter: (a, b) => a.spendWeeklyAvg - b.spendWeeklyAvg,
      },
      itemsCol('itemCount'),
    ];
    return cols;
  };

  function headerPeriodShort(): string {
    if (!activeRange) return '';
    return periodLabel(weekStartOf(activeRange.endDate), activeRange.endDate);
  }

  // ---- Expand: click a week -> its items ----------------------------------
  const weeklyDetailColumns: ColumnsType<WebstaurantWeekDetail> = [
    {
      title: 'Week',
      dataIndex: 'weekEnding',
      key: 'weekEnding',
      render: (end: string, _r, i: number) => (
        <span>
          {periodLabel(weekStartOf(end), end)}
          {i === 0 && (
            <Tag color="#7a1f2a" style={{ marginLeft: 8 }}>
              Latest
            </Tag>
          )}
        </span>
      ),
    },
    { title: 'Spend', dataIndex: 'spend', key: 'spend', align: 'right', render: (v: number) => formatCurrency(v) },
    { title: 'Items', dataIndex: 'itemCount', key: 'itemCount', align: 'right' },
  ];

  const itemColumns: ColumnsType<WebstaurantItemSpend> = [
    {
      title: 'Product',
      dataIndex: 'description',
      key: 'description',
      render: (v: string, r) => (
        <div style={{ minWidth: 200 }}>
          <div>{v || r.itemNumber}</div>
          {r.itemNumber && (
            <Text type="secondary" style={{ fontSize: 11 }}>
              #{r.itemNumber}
            </Text>
          )}
        </div>
      ),
    },
    { title: 'Category', dataIndex: 'category', key: 'category', render: (v: string) => <Tag>{v}</Tag> },
    {
      title: 'Qty',
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      render: (v: number) => (v != null ? Number(v).toLocaleString() : '—'),
      sorter: (a, b) => a.quantity - b.quantity,
    },
    {
      title: 'Spend',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right',
      defaultSortOrder: 'descend',
      render: (v: number) => formatCurrency(v),
      sorter: (a, b) => a.amount - b.amount,
    },
  ];

  const expandable = {
    expandedRowRender: (record: WebstaurantLocation) => {
      const detail = detailByLoc[record.location];
      if (detailLoading[record.location] || !detail) {
        return (
          <div style={{ padding: 24, textAlign: 'center' }}>
            <Spin />
          </div>
        );
      }
      const wks = detail.weeks;
      const sel = selectedWeekByLoc[record.location] ?? wks[0]?.weekEnding ?? '';
      const selWeek = wks.find((w) => w.weekEnding === sel) ?? wks[0];
      return (
        <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 16, padding: '4px 0' }}>
          <div style={{ flex: '0 0 auto', minWidth: isMobile ? 'auto' : 340 }}>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
              WEEK BY WEEK{' '}
              <Text type="secondary" style={{ fontWeight: 400 }}>
                · tap a week
              </Text>
            </Text>
            <Table
              size="small"
              rowKey="weekEnding"
              columns={weeklyDetailColumns}
              dataSource={wks}
              pagination={wks.length > 12 ? { pageSize: 12, size: 'small' } : false}
              onRow={(w) => ({
                onClick: () => setSelectedWeekByLoc((m) => ({ ...m, [record.location]: w.weekEnding })),
                style: {
                  cursor: 'pointer',
                  background: w.weekEnding === sel ? 'rgba(122,31,42,0.08)' : undefined,
                },
              })}
              style={{ marginTop: 6 }}
            />
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Text type="secondary" style={{ fontSize: 12, fontWeight: 600 }}>
              ITEMS ORDERED · {selWeek ? periodLabel(weekStartOf(selWeek.weekEnding), selWeek.weekEnding) : ''}{' '}
              <Text type="secondary" style={{ fontWeight: 400 }}>
                ({selWeek?.items.length ?? 0} products)
              </Text>
            </Text>
            {selWeek && selWeek.items.length > 0 ? (
              <Table
                size="small"
                rowKey={(r) => `${r.itemNumber}|${r.description}`}
                columns={itemColumns}
                dataSource={selWeek.items}
                pagination={selWeek.items.length > 10 ? { pageSize: 10, size: 'small' } : false}
                scroll={{ x: true }}
                style={{ marginTop: 6 }}
              />
            ) : (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No orders this week" style={{ margin: '12px 0' }} />
            )}
          </div>
        </div>
      );
    },
    onExpand: (expanded: boolean, record: WebstaurantLocation) => {
      if (expanded && !detailByLoc[record.location] && !detailLoading[record.location]) {
        void fetchDetail(record.location);
      }
    },
    rowExpandable: (record: WebstaurantLocation) =>
      record.weeklyBreakdown.length > 0 || record.itemBreakdown.length > 0,
    // Show a real "View / Hide" button instead of the bare expand chevron.
    columnWidth: 96,
    expandIcon: ({ expanded, onExpand, record }: { expanded: boolean; onExpand: (r: WebstaurantLocation, e: React.MouseEvent<HTMLElement>) => void; record: WebstaurantLocation }) => {
      if (!(record.weeklyBreakdown.length > 0 || record.itemBreakdown.length > 0)) return null;
      return (
        <Button
          size="small"
          type={expanded ? 'primary' : 'default'}
          onClick={(e) => onExpand(record, e)}
          icon={expanded ? <UpOutlined /> : <DownOutlined />}
        >
          {expanded ? 'Hide' : 'View'}
        </Button>
      );
    },
  };

  // ---- Summary ------------------------------------------------------------
  const sumStores = (list: WebstaurantLocation[]) => list.reduce((s, l) => s + primarySpend(l), 0);
  const summary = useMemo(() => {
    if (!data) return null;
    return {
      store: sumStores(data.stores),
      corporate: sumStores(data.corporate),
      total: sumStores([...data.stores, ...data.corporate, ...data.newStores]),
      count: data.stores.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, mode]);

  const spendLabel = isWeek ? 'Week' : is8Week ? 'per wk' : mode === 'lastMonth' ? 'Last Month' : mode === 'ytd' ? 'YTD' : 'Range';

  const renderTable = (rows: WebstaurantLocation[], showSales: boolean) => (
    <Table
      rowKey="location"
      size={isMobile ? 'small' : 'middle'}
      columns={buildColumns(showSales)}
      dataSource={rows}
      expandable={expandable}
      pagination={false}
      scroll={{ x: 'max-content' }}
    />
  );

  const periodOptions = [
    {
      label: 'Quick',
      options: [
        { value: 'lastMonth', label: 'Last Month (4 weeks)' },
        { value: '8week', label: '8-Week Average' },
        { value: 'ytd', label: 'Year to Date' },
        { value: 'custom', label: 'Custom range…' },
      ],
    },
    {
      label: 'By week — most recent first',
      options: weeks.map((w) => ({
        value: `week:${rangeKey(w)}`,
        label: periodLabel(weekStartOf(w.endDate), w.endDate),
      })),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <ShopOutlined style={{ fontSize: 24, color: '#7a1f2a' }} />
        <Title level={3} style={{ margin: 0 }}>
          Webstaurant Spend
        </Title>
      </div>

      {/* Controls */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={[16, 16]} align="bottom">
          <Col xs={24} md={10}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Period
            </Text>
            <Select
              style={{ width: '100%' }}
              value={period || undefined}
              onChange={setPeriod}
              options={periodOptions}
              placeholder="Select a period"
              listHeight={360}
            />
            {mode === 'custom' && (
              <RangePicker
                style={{ width: '100%', marginTop: 8 }}
                value={customRange}
                onChange={(vals) => {
                  if (vals && vals[0] && vals[1]) setCustomRange([vals[0], vals[1]]);
                }}
                allowClear={false}
              />
            )}
          </Col>
          <Col xs={24} md={14}>
            <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
              Categories ({selectedCategories.length}/{allCategories.length})
            </Text>
            <Select
              mode="multiple"
              allowClear
              style={{ width: '100%' }}
              value={selectedCategories}
              onChange={setSelectedCategories}
              options={allCategories.map((c) => ({ value: c, label: c }))}
              maxTagCount="responsive"
              placeholder="All categories"
            />
          </Col>
        </Row>
      </Card>

      {error && <Alert type="error" message={error} style={{ marginBottom: 16 }} />}

      {/* Summary */}
      {summary && (
        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title={`Store Spend · ${spendLabel}`} value={summary.store} precision={2} prefix="$" />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title={`Corporate · ${spendLabel}`} value={summary.corporate} precision={2} prefix="$" />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title={`Total · ${spendLabel}`} value={summary.total} precision={2} prefix="$" />
            </Card>
          </Col>
          <Col xs={12} md={6}>
            <Card size="small">
              <Statistic title="Stores reporting" value={summary.count} suffix={headerPeriod ? ` · ${headerPeriod}` : ''} />
            </Card>
          </Col>
        </Row>
      )}

      <Spin spinning={loading}>
        {data && data.stores.length === 0 && data.corporate.length === 0 ? (
          <Empty description="No Webstaurant spend for this period" />
        ) : (
          <>
            {isRange ? (
              // Last Month / Custom: sales windows don't align, so show one flat store table.
              data && data.stores.length > 0 && (
                <Card
                  style={{ marginBottom: 16 }}
                  title={<Text strong>Stores</Text>}
                  extra={<Text type="secondary">{data.stores.length} stores</Text>}
                >
                  {renderTable([...data.stores].sort((a, b) => primarySpend(b) - primarySpend(a)), false)}
                </Card>
              )
            ) : (
              // Week / 8-week: group by sales tier like the dashboard.
              TIER_CONFIG.map((tier) => {
                const rows = (storesByTier[tier.key] || []).slice().sort((a, b) => primarySpend(b) - primarySpend(a));
                if (rows.length === 0) return null;
                const tierSpend = rows.reduce((s, r) => s + primarySpend(r), 0);
                return (
                  <Card
                    key={tier.key}
                    style={{ marginBottom: 16 }}
                    title={
                      <Space wrap>
                        <Tag color={tier.color}>{tier.name}</Tag>
                        <Text type="secondary">{tier.range}</Text>
                        <Text type="secondary">· {rows.length} stores</Text>
                      </Space>
                    }
                    extra={<Text strong>{formatCurrency(tierSpend)}</Text>}
                  >
                    {renderTable(rows, true)}
                  </Card>
                );
              })
            )}

            {data && data.corporate.length > 0 && (
              <Card
                style={{ marginBottom: 16 }}
                title={
                  <Space wrap>
                    <Tag color="#444">Corporate / Commissary</Tag>
                    <Text type="secondary">Central orders — not a store</Text>
                  </Space>
                }
              >
                {renderTable([...data.corporate].sort((a, b) => primarySpend(b) - primarySpend(a)), false)}
              </Card>
            )}

            {data && data.newStores.length > 0 && (
              <Card
                style={{ marginBottom: 16 }}
                title={
                  <Space wrap>
                    <Tag color="gold">New Stores</Tag>
                  </Space>
                }
              >
                {renderTable([...data.newStores].sort((a, b) => primarySpend(b) - primarySpend(a)), false)}
              </Card>
            )}
          </>
        )}
      </Spin>
    </div>
  );
};

export default Webstaurant;
